import { useCallback, useEffect, useState } from "react";
import { Cable, CheckCircle2, Download, RefreshCw, TriangleAlert } from "lucide-react";
import { detectVioPortalBridge } from "../services/vioPortalBridge";

const compareVersions = (left, right) => {
  const a = String(left || "0").split(".").map(Number);
  const b = String(right || "0").split(".").map(Number);
  for (let index = 0; index < Math.max(a.length, b.length); index += 1) {
    const difference = (a[index] || 0) - (b[index] || 0);
    if (difference) return difference;
  }
  return 0;
};

export default function VioBridgeDownloadCard({ compact = false, header = false, themeMode = "light" }) {
  const [status, setStatus] = useState("checking");
  const [installedVersion, setInstalledVersion] = useState("");
  const [latestVersion, setLatestVersion] = useState("");
  const [expanded, setExpanded] = useState(false);
  const [headerPinned, setHeaderPinned] = useState(false);
  const isDark = themeMode === "dark";

  const checkBridge = useCallback(async () => {
    setStatus("checking");
    try {
      const [bridge, versionResponse] = await Promise.all([
        detectVioPortalBridge(),
        fetch("/vio-operator-bridge-version.json", { cache: "no-store" }).then((response) =>
          response.ok ? response.json() : {}
        )
      ]);
      const currentVersion = String(bridge?.version || "");
      const newestVersion = String(versionResponse?.version || "");
      setInstalledVersion(currentVersion);
      setLatestVersion(newestVersion);
      setStatus(newestVersion && compareVersions(currentVersion, newestVersion) < 0 ? "outdated" : "ready");
    } catch {
      setInstalledVersion("");
      try {
        const response = await fetch("/vio-operator-bridge-version.json", { cache: "no-store" });
        const data = response.ok ? await response.json() : {};
        setLatestVersion(String(data?.version || ""));
      } catch {
        setLatestVersion("");
      }
      setStatus("missing");
    }
  }, []);

  useEffect(() => {
    checkBridge();
  }, [checkBridge]);

  const needsAction = status === "missing" || status === "outdated";
  const title = status === "missing"
    ? "Vio Köprü bu tarayıcıda bulunamadı"
    : status === "outdated"
      ? "Vio Köprü güncellemesi gerekiyor"
      : status === "ready"
        ? "Vio Köprü kullanıma hazır"
        : "Vio Köprü kontrol ediliyor";

  if (header) {
    const headerOpen = expanded || headerPinned;
    return (
      <div
        className={`vio-bridge-header${headerOpen ? " is-open" : ""}${needsAction ? " needs-action" : ""}`}
        onMouseEnter={() => setExpanded(true)}
        onMouseLeave={() => setExpanded(false)}
        onFocus={() => setExpanded(true)}
        onBlur={(event) => {
          if (!event.currentTarget.contains(event.relatedTarget) && !headerPinned) setExpanded(false);
        }}
      >
        <button
          type="button"
          className="vio-bridge-header__trigger"
          onClick={() => setHeaderPinned((value) => !value)}
          aria-expanded={headerOpen}
          title={title}
        >
          <Cable size={18} />
          <span className="vio-bridge-header__dot" aria-hidden="true" />
          <span className="vio-bridge-header__label">Vio Köprü</span>
        </button>
        {headerOpen && (
          <div className="vio-bridge-header__panel">
            <div className="vio-bridge-header__status">
              {status === "ready" ? <CheckCircle2 size={20} /> : <TriangleAlert size={20} />}
              <div>
                <strong>{title}</strong>
                <span>{installedVersion ? `Kurulu: v${installedVersion}` : "Uzantı algılanmadı"}{latestVersion ? ` · Güncel: v${latestVersion}` : ""}</span>
              </div>
            </div>
            <div className="vio-bridge-header__buttons">
              <a href="/vio-operator-bridge.zip" download><Download size={16} /> İndir</a>
              <button type="button" onClick={checkBridge} disabled={status === "checking"}><RefreshCw size={15} /> Kontrol et</button>
            </div>
            <div className="vio-bridge-header__steps">
              <strong>Nasıl kurulur?</strong>
              <ol>
                <li>ZIP dosyasını bir klasöre çıkarın.</li>
                <li><code>chrome://extensions</code> sayfasını açın.</li>
                <li>Geliştirici modunu açın.</li>
                <li>“Paketlenmemiş öğe yükle” ile çıkan klasörü seçin.</li>
                <li>Argevio ve ARGEPORTAL sekmelerini yenileyin.</li>
              </ol>
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <section
      className={`vio-bridge-card is-collapsible${compact ? " is-compact" : ""}${expanded ? " is-expanded" : ""}${needsAction ? " needs-action" : ""}${isDark ? " is-dark" : ""}`}
      onMouseEnter={() => setExpanded(true)}
      onMouseLeave={() => setExpanded(false)}
      onFocus={() => setExpanded(true)}
      onBlur={(event) => {
        if (!event.currentTarget.contains(event.relatedTarget)) setExpanded(false);
      }}
    >
      <div className="vio-bridge-card__summary">
        <div className="vio-bridge-card__icon" aria-hidden="true">
          {status === "ready" ? <CheckCircle2 size={24} /> : needsAction ? <TriangleAlert size={24} /> : <Cable size={24} />}
        </div>
        <div className="vio-bridge-card__copy">
          <span className="vio-bridge-card__eyebrow">ARGEPORTAL BAĞLANTISI</span>
          <strong>{title}</strong>
          <span>
            {needsAction
              ? "ARGEPORTAL verilerini alabilmek için güncel Chrome uzantısını indirip kurun."
              : `Kurulu sürüm: ${installedVersion || "algılandı"}${latestVersion ? ` · Güncel sürüm: ${latestVersion}` : ""}`}
          </span>
        </div>
        <div className="vio-bridge-card__actions">
          <a className="vio-bridge-download" href="/vio-operator-bridge.zip" download>
            <Download size={17} /> {status === "outdated" ? "Güncellemeyi indir" : "Vio Köprü'yü indir"}
            {latestVersion ? <small>v{latestVersion}</small> : null}
          </a>
          <button type="button" className="vio-bridge-recheck" onClick={checkBridge} disabled={status === "checking"}>
            <RefreshCw size={16} className={status === "checking" ? "is-spinning" : ""} /> Yeniden kontrol et
          </button>
        </div>
      </div>
      <details className="vio-bridge-installation" open={expanded}>
        <summary onClick={(event) => { event.preventDefault(); setExpanded((value) => !value); }}>Vio Köprü nasıl kurulur?</summary>
        <ol>
          <li>İndirilen ZIP dosyasını bilgisayarınızda bir klasöre çıkarın.</li>
          <li>Chrome adres çubuğuna <code>chrome://extensions</code> yazıp Uzantılar sayfasını açın.</li>
          <li>Sağ üstten <strong>Geliştirici modu</strong> seçeneğini açın.</li>
          <li><strong>Paketlenmemiş öğe yükle</strong> düğmesine basıp ZIP'ten çıkan <code>vio-operator-bridge</code> klasörünü seçin.</li>
          <li>ARGEPORTAL ve Argevio sekmelerini yenileyip <strong>Yeniden kontrol et</strong> düğmesine basın.</li>
        </ol>
      </details>
    </section>
  );
}
